/**
 * AppShell - reusable application shell around routed content.
 *
 * Wrapper around <FlatRoutes> (FlatRoutes-safe): renders the sidebar + the
 * app-wide topbar and takes the routes as `children`.
 *
 * Wrapped in <TopBarTitleProvider> so any routed page can set the topbar
 * title/subtitle via useTopBarTitle(). Both the topbar (reader) and the pages
 * (writers) live inside the provider.
 */
import { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Sidebar, SidebarProps } from './Sidebar';
import { TopBarTitleProvider } from './TopBarTitleContext';
import { ShellNavConfig } from './navTypes';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    height: '100vh',
    width: '100%',
    background: theme.palette.background.default,
    overflow: 'hidden',
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  topbarSlot: { flexShrink: 0 },
  body: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'auto',
  },
  '@global': {
    '#root main:first-of-type > main[data-backstage-core-page]': {
      height: '100%',
    },
  },
}));

export type AppShellProps = {
  nav: ShellNavConfig;
  /** The app-wide topbar. Rendered once, above the routed content. */
  topbar?: ReactNode;
  sidebarProps?: Omit<SidebarProps, 'nav'>;
  children: ReactNode;
};

export function AppShell({
  nav,
  topbar,
  sidebarProps,
  children,
}: AppShellProps) {
  const classes = useStyles();
  return (
    <TopBarTitleProvider>
      <div className={classes.root}>
        <Sidebar nav={nav} {...sidebarProps} />
        <div className={classes.main}>
          {topbar && <div className={classes.topbarSlot}>{topbar}</div>}
          <main className={classes.body}>{children}</main>
        </div>
      </div>
    </TopBarTitleProvider>
  );
}
